import styled from '@emotion/styled'

import { ShiftActivity, ShiftHours } from '@bemlo/enums'
import { OnCallTime, shiftWorkDuration } from '@bemlo/schedule'
import { Falsy, Maybe, isTruthy } from '@bemlo/utils'

import { Color, theme } from '../../../../constants'
import {
  Checkbox,
  Clickable,
  Div,
  Flex,
  Icon,
  Text,
  Tooltip,
} from '../../../../elements'
import { useTranslations } from '../../../../translate'
import { SHIFT_HOURS_STYLES } from '../../../../utils/shift-hours-styles'
import { useSelectedShifts } from '../useSchedule'

const Container = styled(Flex)<{ selected: boolean }>(({ selected }) => ({
  flexDirection: 'column',
  gap: 1,
  paddingTop: theme.spacing[1],
  paddingBottom: theme.spacing[1],
  paddingRight: theme.spacing[2],
  paddingLeft: theme.spacing[2],
  height: '100%',
  position: 'relative',

  '.checkbox': {
    display: selected ? 'block' : 'none',
  },

  ':hover': {
    '.checkbox': {
      display: 'block',
    },
  },
}))

const PropertyBadge = styled.div({
  background: Color.LIGHT_GREY,
  border: `0.5px solid ${Color.MEDIUM_GREY}`,
  color: Color.DARKEST_MEDIUM_GREY,
  borderRadius: 4,
  padding: `0px 4px`,
  fontSize: 11,
  fontWeight: theme.font.weight.book,
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  letterSpacing: '0.5px',
})

const SaveBadge = styled(Div)({
  position: 'absolute',
  top: 0,
  right: 0,
  fontWeight: theme.font.weight.book,
  padding: `${theme.spacing['0.5']}px`,
  lineHeight: 1,
})

export type ShiftPresetPersist = {
  id: Maybe<string>
  onClickSave: () => void
}

type VacanciesTitleProps = {
  title?: string
  shiftHours: ShiftHours | null
  breakTime: number
  endTime: string
  shiftIds: string[]
  startDate: string
  startTime: string
  comment: string
  onCallTime?: OnCallTime | null
  tags?: { id: string }[]
  persisted: ShiftPresetPersist | Falsy
  showBreakBadge: boolean
}

export const VacanciesTitle = (props: VacanciesTitleProps) => {
  const t = useTranslations()

  const { allShiftsSelected, toggleSelected, enableSelectShifts } =
    useSelectedShifts(props.shiftIds)

  const shift = {
    activity: ShiftActivity.WORK,
    breakTime: props.breakTime,
    endTime: props.endTime,
    startTime: props.startTime,
    date: props.startDate,
    comment: props.comment,
    onCallTime: props.onCallTime,
  }

  const shiftHoursDesign =
    props.shiftHours && SHIFT_HOURS_STYLES[props.shiftHours]
  const time = shiftWorkDuration(shift)
  const workHours = time.shift.subtract(time.break)
  const numberOfShifts = props.shiftIds.length

  const breakBadge = props.showBreakBadge ? (
    <PropertyBadge>
      <Icon name="CoffeeIcon" size="xs" design="solid" />
      {props.breakTime}m
    </PropertyBadge>
  ) : null

  const commentBadge = props.comment ? (
    <PropertyBadge>
      <Icon name="AnnotationIcon" size="xs" design="solid" />
    </PropertyBadge>
  ) : null

  const tagsBadge = props.tags?.length ? (
    <PropertyBadge>
      <Icon name="TagIcon" size="xs" design="solid" />
      {props.tags.length}
    </PropertyBadge>
  ) : null

  const onCallTimeBadge = props.onCallTime ? (
    <PropertyBadge>
      {t.$date.formatDuration({ hours: time.onCall.asHours() })}{' '}
      {t.$onCallTypeAbbreviation(props.onCallTime.type)}
    </PropertyBadge>
  ) : null

  const hasBadges =
    !!breakBadge || !!commentBadge || !!tagsBadge || !!onCallTimeBadge

  return (
    <Container selected={allShiftsSelected}>
      {!!numberOfShifts && (
        <Text
          color={Color.DARK_GREY}
          fontSize={theme.font.size[6.5]}
          fontWeight={theme.font.weight.book}
        >
          {props.title ?? t.shifts.vacant_other()}
        </Text>
      )}
      <Flex>
        <Text
          fontSize={theme.font.size[6.5]}
          color={allShiftsSelected ? Color.MEDIUM_BLUE : Color.DARK_GREY}
          bold
        >
          {props.startTime} - {props.endTime}
        </Text>
        {enableSelectShifts && !!numberOfShifts && (
          <Div className="checkbox" styles={{ position: 'relative' }}>
            <Checkbox
              styles={{ ml: 1.5, position: 'absolute' }}
              size="sm"
              checked={allShiftsSelected}
              onChange={toggleSelected}
            />
          </Div>
        )}
      </Flex>
      <Text
        fontSize={theme.font.size['6.5']}
        color={Color.DARKEST_MEDIUM_GREY}
        fontWeight={theme.font.weight.book}
      />
      {hasBadges && (
        <Flex gap={1} styles={{ mb: 1.5 }}>
          {breakBadge}
          {commentBadge}
          {tagsBadge}
          {onCallTimeBadge}
        </Flex>
      )}
      {!!numberOfShifts && shiftHoursDesign && (
        <Flex gap={1}>
          <Icon
            name={shiftHoursDesign.icon}
            size="sm"
            color={shiftHoursDesign.color}
          />
          <Text
            color={shiftHoursDesign.color}
            fontSize={theme.font.size['6.5']}
          >
            {numberOfShifts
              ? t.$date.formatDuration({
                  hours: workHours.asHours() * numberOfShifts,
                })
              : `0 ${t.shifts.shift_other().toLocaleLowerCase()}`}
          </Text>
        </Flex>
      )}
      {isTruthy(props.persisted) && (
        <SaveBadge>
          <Tooltip
            side="right"
            text={
              props.persisted.id
                ? t.shifts.remove_fixed()
                : t.shifts.make_fixed()
            }
          >
            <Tooltip.Trigger asChild>
              <Clickable onClick={props.persisted.onClickSave}>
                <Icon
                  name="BookmarkIcon"
                  size="sm"
                  design={props.persisted.id ? 'solid' : 'outline'}
                  color={
                    props.persisted.id ? Color.BLUE : Color.DARK_MEDIUM_GREY
                  }
                />
              </Clickable>
            </Tooltip.Trigger>
          </Tooltip>
        </SaveBadge>
      )}
    </Container>
  )
}
